import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

const VIDEO_EXTS = new Set(['mp4', 'mov', 'm4v', 'qt']);

export function evidenceKind(path: string): 'image' | 'video' {
  const ext = path.split('.').pop()?.toLowerCase() ?? '';
  return VIDEO_EXTS.has(ext) ? 'video' : 'image';
}

export interface ResolvedEvidence {
  path: string;
  signedUrl: string | null;
  kind: 'image' | 'video';
}

/**
 * Given an array of storage paths from the private incident-evidence bucket,
 * generates short-lived signed URLs for rendering in the detail drawer.
 * Only call when the drawer is open — do NOT prefetch for the queue table.
 */
export function useEvidenceSignedUrls(paths: string[] | null | undefined) {
  const [resolved, setResolved] = useState<ResolvedEvidence[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!paths?.length) {
      setResolved([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    Promise.all(
      paths.map(async (path) => {
        try {
          const { data, error } = await supabase.storage
            .from('incident-evidence')
            .createSignedUrl(path, 3600);
          return {
            path,
            signedUrl: error ? null : data?.signedUrl ?? null,
            kind: evidenceKind(path),
          };
        } catch {
          return {
            path,
            signedUrl: null,
            kind: evidenceKind(path),
          };
        }
      }),
    ).then((rows) => {
      if (!cancelled) {
        setResolved(rows);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(paths)]);

  return { resolved, loading };
}
